import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Chip,
  Stack,
  LinearProgress,
  Avatar,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Public, LocationOn, Wifi, Speed, Language, TrendingUp, Storage, Router, CellTower } from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import CoverageMap from '../components/home/CoverageMap';

const Coverage = () => {
  const theme = useTheme();

  const regions = [
    { name: 'North America', coverage: 95, countries: 3, pops: 85, latency: '< 20ms', color: '#e60108' },
    { name: 'Europe', coverage: 92, countries: 45, pops: 120, latency: '< 25ms', color: '#191919' },
    { name: 'Asia Pacific', coverage: 88, countries: 38, pops: 95, latency: '< 40ms', color: '#e60108' },
    { name: 'Middle East', coverage: 85, countries: 12, pops: 35, latency: '< 35ms', color: '#a0a0a0' },
    { name: 'Africa', coverage: 75, countries: 25, pops: 28, latency: '< 50ms', color: '#e60108' },
    { name: 'Latin America', coverage: 82, countries: 20, pops: 42, latency: '< 45ms', color: '#191919' },
  ];

  // Updated stats to match home page data
  const stats = [
    { number: '50,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '100+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '15+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <TrendingUp />, color: '#191919' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Telecom Tower Infrastructure.jpg"
          alt="Global Coverage"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Global Coverage"
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 600,
                letterSpacing: '0.5px',
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Global Coverage
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Connected across 150+ countries with 500,000+ km of fiber network worldwide
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section with Future Goal Note */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Single Future Goal Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                bgcolor: '#f8f8f8',
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 700,
                mx: 'auto',
                border: '1px solid #e6010820',
              }}
            >
              <Typography variant="body1" sx={{ color: '#191919' }}>
                <Box component="span" sx={{ color: '#e60108', fontWeight: 700 }}>🎯 Future Goal:</Box> These statistics represent our ambitious targets for the coming years as we continue to expand and enhance our global communication infrastructure.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Coverage Map */}
      <CoverageMap />

      {/* Coverage Details */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Regional Details"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Regional Coverage Details
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6 }}>
              Detailed coverage information by region
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {regions.map((region, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Card sx={{ p: 3, borderRadius: 3, transition: 'all 0.3s ease' }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: region.color }}>
                      {region.name}
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Coverage</Typography>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: region.color }}>{region.coverage}%</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={region.coverage} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: region.color + '20',
                            '& .MuiLinearProgress-bar': { bgcolor: region.color }
                          }} 
                        />
                      </Box>
                      
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                        <Chip 
                          icon={<Public sx={{ fontSize: 14 }} />} 
                          label={`${region.countries} Countries`} 
                          size="small"
                          sx={{ bgcolor: region.color + '10', color: region.color }}
                        />
                        <Chip 
                          icon={<LocationOn sx={{ fontSize: 14 }} />} 
                          label={`${region.pops} POPs`} 
                          size="small"
                          sx={{ bgcolor: region.color + '10', color: region.color }}
                        />
                        <Chip 
                          icon={<Speed sx={{ fontSize: 14 }} />} 
                          label={`Latency: ${region.latency}`} 
                          size="small"
                          sx={{ bgcolor: region.color + '10', color: region.color }}
                        />
                      </Stack>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Global Presence Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)',
          color: '#fefefe',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Language sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
              Expanding to 180+ Countries by 2025
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              We're constantly growing our network to serve you better
            </Typography>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Coverage;